/**
 * The date field, and the calendar behind it.
 *
 * Two ways in, because people have two habits: type `9/17/26` and carry on, or
 * open the calendar and point. The box takes digits and places its own slashes
 * (`lib/date-mask.ts`), and the calendar is a month you can page through with a
 * month menu, a year menu and an arrow either side.
 *
 * The keyboard model is the APG date-picker dialog, because a calendar is a
 * grid and people expect a grid to behave like one: arrows walk days and
 * weeks, Page Up and Page Down walk months, with Shift they walk years, Home
 * and End are the ends of the week, Enter picks, Escape closes and puts the
 * focus back in the box. Only the focused day is tabbable — a calendar where
 * Tab visits forty-two buttons is a calendar nobody tabs through twice.
 *
 * Sources for the pattern: W3C APG "Date Picker Dialog", MUI X date-picker
 * accessibility notes, React DayPicker's accessibility guide.
 */
import {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react'
import { IconCalendar, IconChevronLeft, IconChevronRight } from '../frame/icons.js'
import { MenuSelect } from './menu-select.js'
import { cn } from '../lib/cn.js'
import { formatDocumentDate, formatListDate } from '../lib/format.js'
import {
  DATE_MASK, completeOnBlur, maskDate, maskFromIso, parseMasked,
} from '../lib/date-mask.js'

export interface DateFieldProps {
  /** `yyyy-mm-dd`, or empty. */
  value: string
  onChange: (value: string) => void
  label: string
  /**
   * 28px in a filter row, 32px beside a chip, **36px on a record form**.
   *
   * `field` is the form kit's control height (`record-flyouts.md` §3), so a
   * date sits level with the text input next to it rather than two pixels
   * shallower.
   */
  size?: 'default' | 'small' | 'field'
  /**
   * What the box reads when nobody is typing in it.
   *
   * `mask` keeps `09/12/2026` — right in a filter row, where the value is a
   * bound being compared and the digits line up with the one beside it.
   * `long` reads `Sep 12, 2026`, which is what a record form should show
   * (`record-flyouts.md` §3, `flyout-field-kit.html`): a date somebody is
   * *reading* should read as a date, and `09/12` is ambiguous to half the
   * world. Either way the mask comes back the moment the field takes focus,
   * because that is what can be typed into. `day` reads `Tue, Apr 14` — a
   * visit's When (Board 8), where the weekday is half of what is decided.
   */
  display?: 'mask' | 'long' | 'day'
  /** The input's own id, so a `Field`'s label has something to point at. */
  id?: string
  className?: string
  disabled?: boolean
}

const SIZES = { default: 'h-8', small: 'h-7', field: 'h-9 rounded-control px-[11px]' } as const

export function DateField({
  value, onChange, label, size = 'default', display = 'mask', id, className, disabled,
}: DateFieldProps) {
  const [text, setText] = useState(() => maskFromIso(value))
  const [open, setOpen] = useState(false)
  const [focused, setFocused] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const shell = useRef<HTMLSpanElement>(null)
  const caret = useRef<number | null>(null)

  /*
   * Follow the value when it changes underneath — a filter cleared from
   * somewhere else, a condition switched from "is" to "is between". Not while
   * the box has focus: re-deriving the text from the value mid-keystroke is
   * how a field fights the person typing in it.
   */
  useEffect(() => {
    if (document.activeElement === input.current) return
    setText(maskFromIso(value))
  }, [value])

  /*
   * What is in the box: always the mask while it has focus, so what is typed
   * is what is seen; the long form otherwise, when asked for and when there
   * is a real date to spell out.
   */
  const parsed = parseMasked(text)
  const shown = !focused && display === 'long' && parsed
    ? formatDocumentDate(parsed)
    : !focused && display === 'day' && parsed ? formatListDate(parsed) : text

  // The caret has to be put back after React has written the new text.
  useLayoutEffect(() => {
    if (caret.current === null) return
    input.current?.setSelectionRange(caret.current, caret.current)
    caret.current = null
  })

  const commit = (next: string) => {
    const iso = parseMasked(next)
    /*
     * An empty box means "no date", which is a value. A half-typed one is not
     * yet anything, so it is left alone rather than reported as a clear.
     *
     * And nothing is reported twice. Leaving the field re-committed whatever
     * was already in it, so tabbing past a date filter re-applied it — a
     * round trip to the server for a value that had not moved.
     */
    const settled = iso !== null ? iso : next.trim() === '' ? '' : null
    if (settled !== null && settled !== value) onChange(settled)
  }

  return (
    <span className={cn('relative inline-flex min-w-0', className)}>
      {/*
        * The whole field is the way in, not the icon.
        *
        * Clicking the box used to put a caret in it and nothing else, so the
        * calendar was reachable only by hitting a 14px glyph on the right.
        * A click anywhere here does both: the caret goes where you clicked
        * and the month opens under it.
        */}
      <span
        ref={shell}
        onMouseDown={(event) => {
          if (disabled) return
          // Not when the click is the icon's own — that one toggles.
          if ((event.target as HTMLElement).closest('button')) return
          setOpen(true)
        }}
        className={cn(
          'flex w-full min-w-0 items-center gap-1 border border-line bg-surface',
          size === 'field' ? '' : 'rounded-field px-2',
          SIZES[size],
          disabled ? 'opacity-50' : 'cursor-text',
          open && 'border-accent-line ring-2 ring-accent-ring',
        )}
      >
        <input
          ref={input}
          id={id}
          value={shown}
          disabled={disabled}
          inputMode="numeric"
          aria-label={label}
          aria-describedby={`${label}-format`}
          placeholder={DATE_MASK}
          onChange={(event) => {
            const box = event.target
            const masked = maskDate(
              box.value, box.selectionStart ?? box.value.length, text)
            caret.current = masked.caret
            setText(masked.text)
            commit(masked.text)
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false)
            const finished = completeOnBlur(text)
            if (finished !== text) setText(finished)
            commit(finished)
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' && !open) { event.preventDefault(); setOpen(true) }
          }}
          className={cn(
            'tnum min-w-0 flex-1 bg-transparent text-text placeholder:text-text-faint focus:outline-none',
            /*
             * At the form kit's height it takes the form kit's type too —
             * 13.5px semibold, the text box's own (`form/field.tsx`) — or a
             * date beside a `1 ▾ month ▾` reads lighter than the controls it
             * sits with (Aaron, review item 5.2).
             */
            size === 'field' ? 'text-[13.5px] font-semibold placeholder:font-medium' : 'text-cell',
          )}
        />
        <span id={`${label}-format`} className="sr-only">Month, day, year</span>

        <button
          type="button"
          disabled={disabled}
          aria-label={`${label}: open the calendar`}
          aria-haspopup="dialog"
          aria-expanded={open}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={() => {
            setOpen((current) => !current)
            input.current?.focus()
          }}
          className="shrink-0 rounded-field p-0.5 text-text-muted hover:bg-line-soft hover:text-text"
        >
          <IconCalendar size={14} />
        </button>
      </span>

      {open && (
        <Calendar
          anchor={shell.current}
          label={label}
          selected={parseMasked(text) ?? value ?? ''}
          onPick={(iso) => {
            setText(maskFromIso(iso))
            onChange(iso)
            setOpen(false)
            input.current?.focus()
          }}
          onClose={() => { setOpen(false); input.current?.focus() }}
        />
      )}
    </span>
  )
}

// --- the calendar ---------------------------------------------------------

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

/** How far either side of this year the year menu reaches. */
const YEAR_SPAN = 12

/** The panel's own size, used to decide whether it opens up or down. */
const PANEL_WIDTH = 252
const PANEL_HEIGHT = 300

const iso = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const fromIso = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

/** Months, clamping the day — 31 January plus a month is 28 February. */
const addMonths = (date: Date, months: number): Date => {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1)
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), last))
}

/**
 * The month on its own, for a control that is not a date box — the When
 * block's **+ Add date** (visits §4.5), which picks a day into a list.
 */
export { Calendar as DateCalendar }

function Calendar({
  anchor, label, selected, onPick, onClose,
}: {
  anchor: HTMLElement | null
  label: string
  selected: string
  onPick: (iso: string) => void
  onClose: () => void
}) {
  const today = useMemo(() => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), now.getDate()) }, [])
  const chosen = fromIso(selected)
  /*
   * One piece of state, not three. The focused day *is* the month and the
   * year: paging to March is moving the focus into March, so a separate
   * "which month am I looking at" can only ever disagree with it.
   */
  const [cursor, setCursor] = useState<Date>(chosen ?? today)
  const panel = useRef<HTMLDivElement>(null)
  const focused = useRef<HTMLButtonElement>(null)
  const [moved, setMoved] = useState(false)

  /*
   * Placed `fixed` from the field's rectangle rather than absolutely inside
   * it. These open inside the filter popover, which is `overflow: hidden` so
   * its own corners stay round — and an absolutely-placed calendar in there
   * was a month header with no month under it.
   */
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null)
  useLayoutEffect(() => {
    const rect = anchor?.getBoundingClientRect()
    if (!rect) return
    const height = panel.current?.getBoundingClientRect().height ?? PANEL_HEIGHT
    const below = window.innerHeight - rect.bottom - 8
    setPlace({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)),
      top: height > below && rect.top > height ? rect.top - height - 4 : rect.bottom + 4,
    })
  }, [anchor])

  useEffect(() => { if (moved) focused.current?.focus() }, [cursor, moved])

  useEffect(() => {
    const away = (event: MouseEvent) => {
      const target = event.target as Node
      if (panel.current?.contains(target)) return
      // The field counts as inside: clicking back into the box to fix the
      // month should not be what shuts the month.
      if (anchor?.contains(target)) return
      onClose()
    }
    const timer = window.setTimeout(() => window.addEventListener('mousedown', away), 0)
    return () => { window.clearTimeout(timer); window.removeEventListener('mousedown', away) }
  }, [onClose, anchor])

  const move = (next: Date) => { setCursor(next); setMoved(true) }

  const onKey = (event: React.KeyboardEvent) => {
    const keys: Record<string, () => Date> = {
      ArrowLeft: () => addDays(cursor, -1),
      ArrowRight: () => addDays(cursor, 1),
      ArrowUp: () => addDays(cursor, -7),
      ArrowDown: () => addDays(cursor, 7),
      Home: () => addDays(cursor, -cursor.getDay()),
      End: () => addDays(cursor, 6 - cursor.getDay()),
      PageUp: () => addMonths(cursor, event.shiftKey ? -12 : -1),
      PageDown: () => addMonths(cursor, event.shiftKey ? 12 : 1),
    }
    const next = keys[event.key]
    if (next) { event.preventDefault(); move(next()); return }
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose() }
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onPick(iso(cursor)) }
  }

  // Six rows from the Sunday on or before the first, so the grid never jumps
  // height between months.
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
  const start = addDays(first, -first.getDay())
  const days = Array.from({ length: 42 }, (_, index) => addDays(start, index))

  const years = Array.from(
    { length: YEAR_SPAN * 2 + 1 },
    (_, index) => today.getFullYear() - YEAR_SPAN + index)

  /*
   * `fixed`, and deliberately *not* portalled.
   *
   * It was portalled to document.body to escape the filter popover's
   * `overflow: hidden`, and that broke everything above it: a portalled panel
   * is not a DOM descendant of the popover, so the popover's outside-click
   * handler counted every click on a day — or the month menu, or an arrow —
   * as a click outside itself and shut the lot. `position: fixed` already
   * escapes an ancestor's overflow, which is how `MenuSelect` opens inside
   * the same popover without any of this.
   */
  return (
    <div
      ref={panel}
      role="dialog"
      aria-label={`${label}: choose a date`}
      style={{ left: place?.left ?? 0, top: place?.top ?? 0, width: PANEL_WIDTH }}
      className={cn(
        'fixed z-[60] rounded-card border border-line bg-surface p-2 shadow-popover',
        // Measured before it is placed, so it must not flash at 0,0.
        place ? 'visible' : 'invisible',
      )}
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => move(addMonths(cursor, -1))}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-field text-text-muted hover:bg-line-soft hover:text-text"
        >
          <IconChevronLeft size={15} />
        </button>

        <MenuSelect
          size="small"
          label="Month"
          value={String(cursor.getMonth())}
          onChange={(next) => next !== null && move(
            new Date(cursor.getFullYear(), Number(next), 1))}
          options={MONTHS.map((name, index) => ({ value: String(index), label: name }))}
          minWidth={140}
          className="min-w-0 flex-1"
        />
        <MenuSelect
          size="small"
          label="Year"
          value={String(cursor.getFullYear())}
          onChange={(next) => next !== null && move(
            new Date(Number(next), cursor.getMonth(), 1))}
          options={years.map((year) => ({ value: String(year), label: String(year) }))}
          minWidth={92}
          className="w-[72px] shrink-0"
        />

        <button
          type="button"
          aria-label="Next month"
          onClick={() => move(addMonths(cursor, 1))}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-field text-text-muted hover:bg-line-soft hover:text-text"
        >
          <IconChevronRight size={15} />
        </button>
      </div>

      {/* Screen readers are told where paging has landed, once. */}
      <span aria-live="polite" className="sr-only">
        {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
      </span>

      <div role="grid" aria-label={`${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
        className="mt-2" onKeyDown={onKey}>
        <div role="row" className="flex">
          {WEEKDAYS.map((day, index) => (
            <span
              key={index}
              role="columnheader"
              aria-label={['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][index]}
              className="flex h-6 w-8 items-center justify-center text-sub text-text-faint"
            >
              {day}
            </span>
          ))}
        </div>

        {Array.from({ length: 6 }, (_, week) => (
          <div role="row" key={week} className="flex">
            {days.slice(week * 7, week * 7 + 7).map((day) => {
              const outside = day.getMonth() !== cursor.getMonth()
              const isCursor = iso(day) === iso(cursor)
              const isChosen = chosen !== null && iso(day) === iso(chosen)
              return (
                <span role="gridcell" key={iso(day)} aria-selected={isChosen}>
                  <button
                    ref={isCursor ? focused : undefined}
                    type="button"
                    // Roving tabindex: one stop for the whole grid.
                    tabIndex={isCursor ? 0 : -1}
                    onClick={() => onPick(iso(day))}
                    aria-label={day.toDateString()}
                    aria-current={iso(day) === iso(today) ? 'date' : undefined}
                    className={cn(
                      'tnum flex h-8 w-8 items-center justify-center rounded-field text-cell',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring',
                      isChosen
                        ? 'bg-accent font-semibold text-white'
                        : outside
                          ? 'text-text-faint hover:bg-surface-alt'
                          : 'text-text hover:bg-surface-alt',
                      !isChosen && iso(day) === iso(today) && 'font-semibold text-accent',
                    )}
                  >
                    {day.getDate()}
                  </button>
                </span>
              )
            })}
          </div>
        ))}
      </div>

      <div className="mt-1 flex items-center justify-between border-t border-line-soft pt-1.5">
        <button
          type="button"
          onClick={() => onPick(iso(today))}
          className="text-cell font-semibold text-accent hover:text-accent-dark"
        >
          Today
        </button>
        <button
          type="button"
          onClick={() => onPick('')}
          className="text-cell text-text-muted hover:text-text"
        >
          Clear
        </button>
      </div>
    </div>
  )
}
